import { Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { map, Subject, switchMap, takeUntil } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonType, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import { FormStatus, pnrFormatValidator } from '@fcom/service-forms';

import { PaxTripService } from '../../services';
import { AddFrequentFlyerError, AddFrequentFlyerResponse } from '../../interfaces/pax-trip.interface';

@Component({
  selector: 'fin-loyalty-add-trips',
  templateUrl: './loyalty-add-trips.component.html',
  styleUrls: ['../../assets/styles/common.scss'],
  standalone: false,
})
export class LoyaltyAddTripsComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private paxTripService = inject(PaxTripService);

  readonly FormStatus = FormStatus;
  readonly ButtonType = ButtonType;
  readonly NotificationTheme = NotificationTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  hideError = true;
  formStatus: WritableSignal<FormStatus> = signal(FormStatus.INITIAL);
  form: UntypedFormGroup;
  messageLabel: string;

  private ngDestroyed$ = new Subject();

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      bookingReference: ['', [pnrFormatValidator(), Validators.required]],
    });
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(true);
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.formStatus.set(FormStatus.LOADING);
      this.paxTripService
        .addTrip(this.form.controls.bookingReference.value)
        .pipe(
          switchMap((response: AddFrequentFlyerResponse) =>
            this.paxTripService.verifyAddedTrip(this.form.controls.bookingReference.value).pipe(
              map((tripAdded: boolean) => {
                if (!tripAdded) {
                  throw new Error('Added trip not found on the bookings list');
                }
                return tripAdded && response;
              })
            )
          ),
          takeUntil(this.ngDestroyed$)
        )
        .subscribe({
          next: () => {
            this.formStatus.set(FormStatus.COMPLETE);
            this.messageLabel = 'myTrips.addTrips.successMessage';
          },
          // eslint-disable-next-line rxjs-x/no-implicit-any-catch
          error: (response: AddFrequentFlyerResponse) => {
            this.formStatus.set(FormStatus.ERROR);
            let messageLabel = 'myTrips.addTrips.errorMessage';

            if (response?.error?.status) {
              if (
                response.error.status.message === AddFrequentFlyerError.BOOKING_NOT_FOUND ||
                response.error.status.message === AddFrequentFlyerError.PASSENGER_NOT_FOUND
              ) {
                messageLabel = 'myTrips.addTrips.notFoundMessage';
              } else if (response.error.status.message === AddFrequentFlyerError.GROUP_BOOKING) {
                messageLabel = 'myTrips.addTrips.groupBookingMessage';
              }
            }

            this.messageLabel = messageLabel;
          },
        });
    }
  }
}
